import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const LANGUAGES = [
  { code: "en", label: "EN", full: "English" },
  { code: "ro", label: "RO", full: "Română" },
  { code: "ru", label: "RU", full: "Русский" },
];

interface LanguageSwitcherProps {
  transparent?: boolean;
}

export default function LanguageSwitcher({ transparent = false }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentCode = LANGUAGES.some((l) => l.code === i18n.language) ? i18n.language : "en";
  const current = LANGUAGES.find((l) => l.code === currentCode) ?? LANGUAGES[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChange = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        aria-label="Switch language"
        className={`flex items-center gap-1.5 text-xs font-semibold tracking-wide px-3 py-1.5 rounded-md border transition-colors duration-200 cursor-pointer whitespace-nowrap ${
          transparent
            ? "text-white/80 border-white/20 hover:text-white hover:border-white/40"
            : "text-navy/70 border-cool-gray-200 hover:text-navy hover:border-cool-gray-300"
        }`}
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-global-line text-sm" />
        </span>
        <span>{current.label}</span>
        <span className="w-3 h-3 flex items-center justify-center">
          <i
            className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
          />
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-36 bg-white border border-cool-gray-200 rounded-md overflow-hidden z-50">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer whitespace-nowrap ${
                currentCode === lang.code
                  ? "bg-navy text-white"
                  : "text-navy hover:bg-cool-gray-50"
              }`}
            >
              <span className="text-xs font-bold">{lang.label}</span>
              <span className="text-xs opacity-70">{lang.full}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
